import { useState, useCallback, useRef, useEffect } from 'react';
import * as apiUtility from '../../../config/APIUtils';
import {
    BASE_URL_GET_ALLCOURSE_VERSIONS,
    VALIDATE_COURSE_SUBMISSION_URL,
    COURSE_ENROLLMENT_URL,
    URL_UPDATE_TP_CONSENT,
    CARD_STATES,
    SUBMISSION_STEPS,
    SUBMISSION_PHASES,
} from './constants';
import { useUserCourse } from '../../../config/UserCourseContext';


export function useCardStates(versionIds = []) {
    const [cardStates, setCardStates] = useState({});
    const [expandedVersionId, setExpandedVersionId] = useState(null);

    const initStates = useCallback((ids) => {
        const init = {};
        ids.forEach(id => {
            init[id] = { state: CARD_STATES.IDLE, errorMessage: null, course_url: null };
        });
        setCardStates(prev => ({ ...prev, ...init }));
    }, []);

    const setCardMeta = useCallback((versionId, patch) => {
        setCardStates(prev => ({
            ...prev,
            [versionId]: { ...prev[versionId], ...patch },
        }));
    }, []);

    const getCardMeta = useCallback((versionId) => {
        return cardStates[versionId] ?? { state: CARD_STATES.IDLE, errorMessage: null, course_url: null };
    }, [cardStates]);

    const collapseCard = useCallback((id) => {
        setCardMeta(id, { state: CARD_STATES.IDLE, errorMessage: null, course_url: null });
    }, [setCardMeta]);

    const expandCard = useCallback((id) => {
        setCardMeta(id, { state: CARD_STATES.SUCCESS });
    }, [setCardMeta]);

    const expandView = useCallback((versionId) => {
        setExpandedVersionId(versionId);
    }, []);

    const collapseView = useCallback(() => {
        setExpandedVersionId(null);
    }, []);

    return {
        cardStates,
        expandedVersionId,
        initStates,
        setCardMeta,
        getCardMeta,
        collapseCard,
        expandCard,
        expandView,
        collapseView,
    };
}


export function useCourseVersions(crsId, userId) {
    const [courseList, setCourseList] = useState([]);
    const [submittedVersions, setSubmittedVersions] = useState({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchCourseVersions = useCallback(async () => {
        if (!crsId || !userId) return;
        setLoading(true);
        setError(null);
        try {
            const url = BASE_URL_GET_ALLCOURSE_VERSIONS + crsId + '/' + userId;
            const req = { ...apiUtility.apiInputRequest, endPoint: url };
            const res = await apiUtility.fetchAPIDataGET(req);
            setCourseList(res.data);
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || 'Failed to load course versions');
        } finally {
            setLoading(false);
        }
    }, [crsId, userId]);

    useEffect(() => {
        fetchCourseVersions();
    }, [fetchCourseVersions]);

    const markSubmitted = useCallback((versionId, trainingUrl) => {
        setSubmittedVersions(prev => ({ ...prev, [versionId]: { course_url: trainingUrl || null } }));
    }, []);

    const isSubmitted = useCallback((versionId) => {
        return !!submittedVersions[versionId];
    }, [submittedVersions]);

    const getSubmittedTrainingUrl = useCallback((versionId) => {
        return submittedVersions[versionId]?.course_url || null;
    }, [submittedVersions]);

    return {
        courseList,
        submittedVersions,
        loading,
        error,
        markSubmitted,
        isSubmitted,
        getSubmittedTrainingUrl,
        refetch: fetchCourseVersions,
    };
}


export function useSubmissionFlow({ userId }) {
    const [submissionStep, setSubmissionStep] = useState(null);
    const [submissionPhase, setSubmissionPhase] = useState(null);
    const [errorMessage, setErrorMessage] = useState(null);
    const [pendingByVersion, setPendingByVersion] = useState({});
    const [consentVersionId, setConsentVersionId] = useState(null);

    const { addEnrolledCourse } = useUserCourse();

    const isSubmittingRef = useRef(false);

    const createCourseObj = useCallback((r) => ({
        courseId: r.crsId,
        courseVersionId: r.crsVersionId,
        tp_course_code: r.tp_course_code,
        title: r.title,
        tp_id: parseInt(r.orgIssued),
        versionNo: r.versionNo,
        trainDuration: r.trainDuration,
        equivalentCrsStatus: r.equivalentCrsStatus,
        training_url: r.training_url,
        submitted_date: '',
        validity: r.validity,
        payeeHome: '',
        role: '',
        status: false,
    }), []);

    const validateCourse = useCallback(async (courseObj, avoidWarning = false, avoidTPCheck = false) => {
        const req = {
            ...apiUtility.apiInputRequest,
            endPoint: VALIDATE_COURSE_SUBMISSION_URL,
            data: { userId, courseObj, avoidWarning, avoidTPCheck },
        };
        const res = await apiUtility.sendAPIDataPOST(req);
        return res.data;
    }, [userId]);

    const enrollCourse = useCallback(async (courseObj) => {
        const req = {
            ...apiUtility.apiInputRequest,
            endPoint: COURSE_ENROLLMENT_URL,
            data: { userId, cartCourseArr: [courseObj], isAdmin: true },
        };
        const res = await apiUtility.sendAPIDataPOST(req);
        return res.data;
    }, [userId]);

    const updateTPConsent = useCallback(async (tpIds) => {
        const req = {
            ...apiUtility.apiInputRequest,
            endPoint: URL_UPDATE_TP_CONSENT,
            data: { userId, tpIds },
        };
        await apiUtility.sendAPIDataPOST(req);
    }, [userId]);

    /**
     * Full submission flow in clear sequential phases:
     *   PROCESSING → VALIDATING → SUBMITTING → SUCCESS/ERROR
     */
    const executeFlow = useCallback(async (
        courseObj,
        options = { avoidWarning: false, avoidTPCheck: false }
    ) => {
        if (isSubmittingRef.current) {
            console.warn('Submission already in progress');
            return { success: false, errorMessage: 'Submission already in progress' };
        }
        isSubmittingRef.current = true;

        try {
            setSubmissionPhase(SUBMISSION_PHASES.PROCESSING);
            await new Promise(r => setTimeout(r, 400));

            setSubmissionPhase(SUBMISSION_PHASES.VALIDATING);
            const validationResult = await validateCourse(courseObj, options.avoidWarning, options.avoidTPCheck);

            //console.log("Validation result for enrolled course: ", validationResult);

            if (validationResult.warning14days) {
                setSubmissionStep(SUBMISSION_STEPS.WARNING_14DAYS);
                setSubmissionPhase(null);
                isSubmittingRef.current = false;
                return {
                    success: false,
                    warningType: 'warning14days',
                    courseObj,
                };
            }

            if (validationResult.trainingProviderError) {
                setSubmissionStep(SUBMISSION_STEPS.CONSENT_REQUIRED);
                setSubmissionPhase(null);
                isSubmittingRef.current = false;
                return {
                    success: false,
                    warningType: 'trainingProviderError',
                    courseObj,
                };
            }

            await new Promise(r => setTimeout(r, 600));

            setSubmissionPhase(SUBMISSION_PHASES.SUBMITTING);
            const enrollResult = await enrollCourse(courseObj);

            let enrolled = false;
            let errorReason = "";
            if (enrollResult.responseMap) {
                for (const element of enrollResult.responseMap) {
                    if (element.courseVersionId === courseObj.courseVersionId) {
                        if (element.status) {
                            enrolled = true;

                            let courseObjForProvider = {
                                courseId: element.courseId,
                                courseVersionId: element.courseVersionId,
                                tp_course_code: element.tp_course_code,
                                course_url: element.course_url,
                                submitted_date: element.submitted_date,
                            };
                            //console.log("Adding enrolled course to context: ", courseObjForProvider);
                            addEnrolledCourse(courseObjForProvider);
                        } else {
                            errorReason = element.reason || "Unknown reason";
                        }
                    }
                }
            }

            await new Promise(r => setTimeout(r, 800));

            if (enrolled) {
                let enrolledCourseUrl = null;
                if (enrollResult.responseMap) {
                    for (const element of enrollResult.responseMap) {
                        if (element.courseVersionId === courseObj.courseVersionId) {
                            enrolledCourseUrl = element.course_url || null;
                            break;
                        }
                    }
                }

                setSubmissionPhase(SUBMISSION_PHASES.SUCCESS);
                setSubmissionStep(SUBMISSION_STEPS.SUCCESS);
                isSubmittingRef.current = false;
                return { success: true, course_url: enrolledCourseUrl };
            } else {
                setSubmissionPhase(SUBMISSION_PHASES.ERROR);
                setSubmissionStep(SUBMISSION_STEPS.ERROR);
                const msg = errorReason || 'Enrollment failed.';
                setErrorMessage(msg);
                isSubmittingRef.current = false;
                return { success: false, errorMessage: msg };
            }
        } catch (err) {
            const msg = err?.response?.data?.message
                || err?.response?.data?.error
                || err?.message
                || 'Unexpected error during submission. Please try again.';
            setSubmissionPhase(SUBMISSION_PHASES.ERROR);
            setSubmissionStep(SUBMISSION_STEPS.ERROR);
            setErrorMessage(msg);
            isSubmittingRef.current = false;
            return { success: false, errorMessage: msg };
        }
    }, [validateCourse, enrollCourse]);

    /** Continue flow after 14-day warning */
    const continueAfterWarning = useCallback(async (courseObj, warningType) => {
        try {
            if (warningType === 'warning14days') {
                setSubmissionPhase(SUBMISSION_PHASES.VALIDATING);
                return await executeFlow(courseObj, { avoidWarning: true, avoidTPCheck: false });
            }
            return { success: false, errorMessage: 'Unknown warning type' };
        } catch (err) {
            const msg = err?.message || 'Error continuing after warning';
            setSubmissionPhase(SUBMISSION_PHASES.ERROR);
            setSubmissionStep(SUBMISSION_STEPS.ERROR);
            setErrorMessage(msg);
            return { success: false, errorMessage: msg };
        }
    }, [executeFlow]);

    /** Handle TP consent result */
    const handleConsentResult = useCallback(async (agreed, courseObj) => {
        if (agreed) {
            try {
                setSubmissionPhase(SUBMISSION_PHASES.VALIDATING);
                await updateTPConsent(courseObj.tp_id);
                return await executeFlow(courseObj, { avoidWarning: false, avoidTPCheck: true });
            } catch (err) {
                const msg = err?.message || 'Error updating consent';
                setSubmissionPhase(SUBMISSION_PHASES.ERROR);
                setSubmissionStep(SUBMISSION_STEPS.ERROR);
                setErrorMessage(msg);
                return { success: false, errorMessage: msg };
            }
        }
        setSubmissionPhase(null);
        setSubmissionStep(null);
        isSubmittingRef.current = false;
        return { success: false, errorMessage: 'Consent declined' };
    }, [executeFlow, updateTPConsent]);

    /** Reset the submission flow */
    const resetFlow = useCallback(() => {
        setSubmissionStep(null);
        setSubmissionPhase(null);
        setErrorMessage(null);
        isSubmittingRef.current = false;
    }, []);

    return {
        submissionStep,
        submissionPhase,
        errorMessage,
        pendingByVersion,
        consentVersionId,
        setConsentVersionId,
        setPendingByVersion,
        createCourseObj,
        executeFlow,
        continueAfterWarning,
        handleConsentResult,
        resetFlow,
    };
}